import React, { createContext, useContext, useState, useEffect } from 'react';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [userInfo, setUserInfo] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [userRole, setUserRole] = useState(null);

  // Initialize user info from localStorage on app start (only non-sensitive data)
  useEffect(() => {
    const storedUserInfo = localStorage.getItem('userInfo');
    const storedUserRole = localStorage.getItem('userRole');

    if (storedUserInfo) {
      setUserInfo(JSON.parse(storedUserInfo));
    }
    if (storedUserRole) {
      setUserRole(storedUserRole);
    }
  }, []);

  // Update user info and store in context (tokens only in memory)
  const updateUserInfo = (loginResponse) => {
    // Handle new nested structure where data is under loginResponse.data
    const responseData = loginResponse.data || loginResponse;
    const { accessToken, refreshToken, user } = responseData;
    
    // Handle both old format (userRole) and new format (user.userRoles array)
    let userRoleData, userRoleString, entityInfo;
    
    if (user?.userRoles && Array.isArray(user.userRoles)) {
      // New format: user.userRoles array
      userRoleData = user.userRoles[0]; // Use first role for now
      userRoleString = userRoleData?.role || 'SUB_MANAGER';
      entityInfo = userRoleData?.entityInfo;
      
      // Store entity information if available
      if (entityInfo) {
        localStorage.setItem('entId', userRoleData.id); // Store the userRole id as entId
        localStorage.setItem('entityName', entityInfo.name);
        localStorage.setItem('entityType', entityInfo.entityType);
      }
    } else if (responseData.userRoles && Array.isArray(responseData.userRoles)) {
      // Alternative: userRoles at root level
      userRoleData = responseData.userRoles[0];
      userRoleString = userRoleData?.role || 'SUB_MANAGER';
      entityInfo = userRoleData?.entityInfo;
      
      if (entityInfo) {
        localStorage.setItem('entId', userRoleData.id);
        localStorage.setItem('entityName', entityInfo.name);
        localStorage.setItem('entityType', entityInfo.entityType);
      }
    } else {
      // Old format: single userRole
      userRoleData = responseData.userRole;
      userRoleString = userRoleData?.role || 'MANAGER';
    }
    
    // Create a userInfo object with portal field for backward compatibility
    const userInfoWithPortal = {
      ...userRoleData,
      portal: 'MANAGER', // Add portal field for Appbar compatibility
      role: userRoleString,
      email: user?.email,
      userId: user?.id
    };
    
    // Store in context state
    setUserInfo(userInfoWithPortal);
    setAccessToken(accessToken);
    setRefreshToken(refreshToken);
    setUserRole(userRoleString);

    // Store only non-sensitive data in localStorage
    localStorage.setItem('userInfo', JSON.stringify(userInfoWithPortal));
    localStorage.setItem('userRole', userRoleString);
  };

  // Clear user info (for logout)
  const clearUserInfo = () => {
    setUserInfo(null);
    setAccessToken(null);
    setRefreshToken(null);
    setUserRole(null);

    // Clear from localStorage
    localStorage.removeItem('userInfo');
    localStorage.removeItem('userRole');
  };

  // Get user role helper
  const getUserRole = () => {
    return userRole;
  };

  // Check if user is manager
  const isManager = () => {
    return userRole === 'MANAGER';
  };

  // Check if user is sub manager
  const isSubManager = () => {
    return userRole === 'SUB_MANAGER';
  };

  // Check if user is investor
  const isInvestor = () => {
    return userRole === 'INVESTOR';
  };

  // Get user ID
  const getUserId = () => {
    return userInfo?.id;
  };

  // Get user role ID
  const getUserRoleId = () => {
    return userInfo?.id;
  };

  const value = {
    userInfo,
    accessToken,
    refreshToken,
    userRole,
    updateUserInfo,
    clearUserInfo,
    getUserRole,
    isManager,
    isSubManager,
    isInvestor,
    getUserId,
    getUserRoleId,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
}; 